import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import { techniqueApi, messageApi, notificationApi } from '../../services/api';
import {
  MessageSquare,
  LayoutDashboard,
  CreditCard,
  Users,
  Settings,
  LogOut,
  Menu,
  X,
  Mail,
  BookOpen,
  Brain,
  Castle,
  Hash,
  Calculator,
  Link2,
  Clock,
  Lightbulb,
  Puzzle,
  Target,
  Star,
  Zap,
  GraduationCap,
  Video,
  Bell,
  Upload,
  ClipboardCheck,
  ChevronDown,
  ChevronRight,
  Sparkles,
  User,
  RefreshCw,
  Sun,
  Moon
} from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../../theme';

// Icon mapping for techniques
const iconMap = {
  brain: Brain,
  castle: Castle,
  hash: Hash,
  calculator: Calculator,
  link: Link2,
  clock: Clock,
  book: BookOpen,
  lightbulb: Lightbulb,
  puzzle: Puzzle,
  target: Target,
  star: Star,
  zap: Zap
};

function TechniqueIcon({ name, className }) {
  const Icon = iconMap[name] || Brain;
  return <Icon className={className} />;
}

function Layout() {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [techniques, setTechniques] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [lmsExpanded, setLmsExpanded] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const notifRef = useRef(null);
  const isChatPage = location.pathname === '/chat' || location.pathname.startsWith('/techniques/');

  // Close notification dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notifRef.current && !notifRef.current.contains(e.target) && !e.target.closest('[data-notif-bell]')) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Allow child pages (e.g. StudentChat) to open the sidebar via custom event
  useEffect(() => {
    const handler = () => setSidebarOpen(true);
    window.addEventListener('open-sidebar', handler);
    return () => window.removeEventListener('open-sidebar', handler);
  }, []);

  useEffect(() => {
    if (user?.role === 'student') {
      loadTechniques();
    }
    loadUnreadCount();
    loadNotificationCount();

    // Poll unread counts every 30 seconds
    const interval = setInterval(() => {
      loadUnreadCount();
      loadNotificationCount();
    }, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const loadTechniques = async () => {
    try {
      const response = await techniqueApi.getTechniques();
      setTechniques(response.data);
    } catch (error) {
      console.error('Failed to load techniques:', error);
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await messageApi.getUnreadCount();
      setUnreadMessages(response.data.unreadCount);
    } catch (error) {
      console.error('Failed to load unread count:', error);
    }
  };

  const loadNotificationCount = async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadNotifications(response.data.count);
    } catch (error) {
      console.error('Failed to load notification count:', error);
    }
  };

  const toggleNotifications = async () => {
    if (!showNotifications) {
      try {
        const response = await notificationApi.list();
        setNotifications(response.data.slice(0, 10));
      } catch (error) {
        console.error('Failed to load notifications:', error);
      }
    }
    setShowNotifications(!showNotifications);
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.is_read) {
      try {
        await notificationApi.markRead(notif.id);
        setUnreadNotifications(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch (error) { /* ignore */ }
    }
    if (notif.link) {
      window.location.href = notif.link;
    }
    setShowNotifications(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationApi.markAllRead();
      setUnreadNotifications(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (error) { /* ignore */ }
  };

  const navigation = {
    student: [
      { name: 'Chat', href: '/chat', icon: MessageSquare },
      { name: 'My Profile', href: '/profile', icon: User },
      { name: 'My Memory Profile', href: '/memory-profile', icon: Sparkles },
      { name: 'Community', href: '/community', icon: Users },
      { name: 'Messages', href: '/messages', icon: Mail, badge: unreadMessages },
      { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
      { name: 'Recharge', href: '/recharge', icon: CreditCard },
    ],
    trainer: [
      { name: 'Dashboard', href: '/trainer', icon: LayoutDashboard },
      { name: 'Techniques', href: '/techniques/manage', icon: BookOpen },
      { name: 'Community', href: '/community', icon: Users },
      { name: 'Messages', href: '/messages', icon: Mail, badge: unreadMessages },
      { name: 'Chat', href: '/chat', icon: MessageSquare },
      { name: 'Recharge', href: '/recharge', icon: CreditCard },
    ],
    admin: [
      { name: 'Admin', href: '/admin', icon: Settings },
      { name: 'Techniques', href: '/techniques/manage', icon: BookOpen },
      { name: 'Community', href: '/community', icon: Users },
      { name: 'Messages', href: '/messages', icon: Mail, badge: unreadMessages },
      { name: 'Chat', href: '/chat', icon: MessageSquare },
    ]
  };

  const lmsLinks = {
    // LMS management is admin-only. Trainers have NO LMS access at all.
    student: [
      { name: 'Courses', href: '/lms/courses', icon: BookOpen },
      { name: 'Assessments', href: '/lms/assessments', icon: ClipboardCheck },
      { name: 'Live Sessions', href: '/lms/live-sessions', icon: Video },
    ],
    trainer: [],   // no LMS access for trainers
    admin: [
      { name: 'Manage LMS', href: '/lms/manage', icon: Settings },
      { name: 'Submissions', href: '/lms/submissions', icon: ClipboardCheck },
    ]
  };

  const links = navigation[user?.role] || navigation.student;
  const lmsSubLinks = lmsLinks[user?.role] || lmsLinks.student;
  const isLmsActive = location.pathname.startsWith('/lms');

  // LMS beta test — normally hidden for students, but a whitelist of student
  // emails can preview it. Once QA passes, remove this whitelist and the
  // `!studentHasLmsAccess` guard below so all students see LMS.
  const LMS_STUDENT_BETA_EMAILS = ['harikishan9963@gmail.com'];
  const studentHasLmsAccess = user?.role === 'student'
    && LMS_STUDENT_BETA_EMAILS.includes((user?.email || '').toLowerCase());

  return (
    <div className="h-dvh flex overflow-hidden">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800">
            <Link to="/" className="text-xl font-bold text-primary-600 dark:text-primary-400">
              Memory Coach
            </Link>
            <div className="flex items-center space-x-1">
              {/* Notification Bell (sidebar - visible on lg+) */}
              <button
                onClick={toggleNotifications}
                className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 relative"
                data-notif-bell
              >
                <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                {unreadNotifications > 0 && (
                  <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </button>

              <button
                className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
                onClick={() => setSidebarOpen(false)}
              >
                <X className="w-5 h-5 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Trainer info for students */}
          {user?.role === 'student' && user?.trainerName && (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-primary-50 dark:bg-gray-800/60">
              <p className="text-xs text-gray-500 dark:text-gray-400">Your Trainer</p>
              <p className="text-sm font-medium text-primary-700 dark:text-primary-300">{user.trainerName}</p>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {links.map((link) => {
              const Icon = link.icon;
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`
                    flex items-center justify-between px-3 py-2 rounded-lg transition-colors
                    ${isActive
                      ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                    }
                  `}
                  onClick={() => setSidebarOpen(false)}
                >
                  <div className="flex items-center">
                    <Icon className="w-5 h-5 mr-3" />
                    {link.name}
                  </div>
                  {link.badge > 0 && (
                    <span className="bg-primary-600 text-white text-xs rounded-full px-2 py-0.5">
                      {link.badge}
                    </span>
                  )}
                </Link>
              );
            })}

            {/* LMS section — admin (full manage) or beta-whitelisted student.
                 Trainers are excluded entirely. */}
            {(user?.role === 'admin' || studentHasLmsAccess) && <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-800">
              <button
                onClick={() => setLmsExpanded(!lmsExpanded)}
                className={`
                  flex items-center justify-between w-full px-3 py-2 rounded-lg transition-colors
                  ${isLmsActive
                    ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}
                `}
              >
                <div className="flex items-center">
                  <GraduationCap className="w-5 h-5 mr-3" />
                  LMS
                </div>
                {lmsExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {lmsExpanded && (
                <div className="mt-1 ml-2 space-y-0.5">
                  {lmsSubLinks.map((link) => {
                    const Icon = link.icon;
                    const isActive = location.pathname === link.href;
                    return (
                      <Link
                        key={link.href}
                        to={link.href}
                        className={`
                          flex items-center px-3 py-1.5 rounded-lg text-sm transition-colors
                          ${isActive
                            ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                            : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-700 dark:hover:text-gray-200'
                          }
                        `}
                        onClick={() => setSidebarOpen(false)}
                      >
                        <Icon className="w-4 h-4 mr-3" />
                        {link.name}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>}

            {/* Technique-specific areas for students */}
            {user?.role === 'student' && techniques.length > 0 && (
              <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-800">
                <p className="px-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider mb-2">
                  Techniques
                </p>
                {techniques.map((tech) => {
                  const isActive = location.pathname === `/techniques/${tech.slug}`;
                  return (
                    <Link
                      key={tech.slug}
                      to={`/techniques/${tech.slug}`}
                      className={`
                        flex items-center px-3 py-2 rounded-lg text-sm transition-colors
                        ${isActive
                          ? 'bg-primary-50 dark:bg-primary-500/15 text-primary-700 dark:text-primary-300'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
                        }
                      `}
                      onClick={() => setSidebarOpen(false)}
                    >
                      <TechniqueIcon name={tech.icon} className="w-4 h-4 mr-3" />
                      {tech.name}
                    </Link>
                  );
                })}
              </div>
            )}
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center mb-3">
              <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-500/20 flex items-center justify-center">
                <span className="text-primary-700 dark:text-primary-300 font-medium">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user?.role}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={logout}
                className="flex-1 flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                <LogOut className="w-4 h-4 mr-3" />
                Logout
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label="Toggle theme"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile header - hidden on chat page which has its own header */}
        <header className={`lg:hidden ${isChatPage ? 'hidden' : 'flex'} items-center justify-between h-16 px-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900`}>
          <div className="flex items-center">
            <button
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5 dark:text-gray-300" />
            </button>
            <span className="ml-3 text-lg font-semibold dark:text-gray-100">Memory Coach</span>
          </div>
          <div className="flex items-center">
            <button
              onClick={() => window.location.reload()}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            </button>
            <button
              onClick={toggleNotifications}
              className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 relative"
              data-notif-bell
            >
              <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              {unreadNotifications > 0 && (
                <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] rounded-full w-4 h-4 flex items-center justify-center">
                  {unreadNotifications > 9 ? '9+' : unreadNotifications}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Page content - chat page manages its own scrolling */}
        <main
          className={`flex-1 ${isChatPage ? 'overflow-hidden' : 'overflow-auto'}`}
          style={{ overscrollBehaviorY: 'contain', WebkitOverflowScrolling: 'touch' }}
        >
          <Outlet />
        </main>
      </div>

      {/* Notification Dropdown - rendered at page level */}
      {showNotifications && (
        <div ref={notifRef} className="fixed top-16 right-4 lg:left-64 lg:right-auto w-80 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-lg z-[100] max-h-96 overflow-y-auto">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Notifications</h3>
            {unreadNotifications > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
              >
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="p-4 text-center text-sm text-gray-500 dark:text-gray-400">
              No notifications
            </div>
          ) : (
            notifications.map(notif => (
              <button
                key={notif.id}
                onClick={() => handleNotificationClick(notif)}
                className={`w-full text-left px-4 py-3 border-b border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                  !notif.is_read ? 'bg-primary-50/50 dark:bg-primary-500/10' : ''
                }`}
              >
                <p className={`text-sm ${!notif.is_read ? 'font-medium text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                  {notif.title}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2">{notif.message}</p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-1">
                  {new Date(notif.created_at).toLocaleDateString()} {new Date(notif.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default Layout;
